import { Injectable } from '@angular/core';
import { ApiService } from '@core/services/api.service';
import { StateService } from '@core/services/state.service';
import { AuthenticationService } from 'app/auth/service';
import { environment } from 'environments/environment';
import {
  BehaviorSubject,
  of,
  Observable,
  throwError,
  forkJoin,
  combineLatest,
} from 'rxjs';
import { switchMap, take, tap, catchError, map, withLatestFrom, finalize } from 'rxjs/operators';
import { Column } from '../models/torzsek';
import { findByProp, parseJSON, stringifyIfJSON } from '../utils';
import { OnelszamoloEgysegekService } from './onelszamolo-egysegek.service';
import { PartnerekService } from './partnerek.service';
import { RaktarakService } from './raktarak.service';
import { ArkategoriakService } from './arkategoriak.service';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { AfakulcsokService } from './afakulcsok.service';
import { nullSafeIsEquivalent } from '@angular/compiler/src/output/output_ast';

@Injectable({
  providedIn: 'root',
})
export class SzallitolevelService {
  private readonly apiEndpoint = 'szallitolevel';
  private readonly arNelkulEndpoint = 'szallitolevel_arnelkul';
  private readonly columnsEndpoint = 'szallitolevel/columns';
  private readonly updateEndpoint = 'service/helplib/bizonylat_frissites/sz';
  private readonly nyomtatasBeallitasEndpoint = 'service/helplib/nyomtatas_kapcsolo/sz';
  private readonly nyitottSzallito = 'service/nyitottszallito/lekeres';
  private readonly osszesitoNyomtatas = 'osszesito_szallito';

  private lastFetch: Date = new Date();
  private szallitolevel = JSON.parse(localStorage.getItem('szallitolevel'));


  private readonly _data = new BehaviorSubject<any[]>(null);
  private readonly _selectedData = new BehaviorSubject<any>(null);

  // public data$ = combineLatest([
  //   this._data,
  //   this.oeService.data$,
  //   this.raktarakService.data$,
  //   this.partnerekService.data$,
  //   this.arkategoriakService.data$
  // ]).pipe(
  //   switchMap(([state, oek, raktarak, partnerek, arkategoriak]) => {
  //     return state
  //       ? of(this.mapSzallitolevelek(state, oek, raktarak, partnerek, arkategoriak))
  //       : this.szallitolevel?.db > 0 ? this.getDBData() : this.fetchData()
  //   }),
  //   withLatestFrom(this.authService.currentUser$),
  //   map(([data, user]) => data.filter((e: any) => e.oe_id === user.oe))
  // );

  public data$ = combineLatest([
    this._data,
    this.oeService.data$,
    this.raktarakService.data$,
    this.partnerekService.data$,
    this.arkategoriakService.data$
  ]).pipe(
    switchMap(([state, oek, raktarak, partnerek, arkategoriak]) =>
      state
        ? of(this.mapSzallitolevelek(state, oek, raktarak, partnerek, arkategoriak))
        : this.fetchData()
    ),
    withLatestFrom(this.authService.currentUser$),
    map(([data, user]) => data.filter((e: any) => e.oe_id === user.oe))
  );

  constructor(
    private oeService: OnelszamoloEgysegekService,
    private partnerekService: PartnerekService,
    private raktarakService: RaktarakService,
    private api: ApiService,
    private stateService: StateService,
    private authService: AuthenticationService,
    private arkategoriakService: ArkategoriakService,
    private afakulcsokService: AfakulcsokService,
    private dbService: NgxIndexedDBService
  ) {}

  get columns$(): Observable<Column[]> {
    return this.getColumns();
  }

  // get initData$(): Observable<any[]> {
  //   return this._data.pipe(
  //     take(1),
  //     switchMap((state: any[]) =>
  //       state
  //         ? forkJoin([
  //             of(state),
  //             this.oeService.initData$,
  //             this.raktarakService.initData$,
  //             this.partnerekService.initData$,
  //             this.arkategoriakService.initData$
  //           ]).pipe(
  //             map(([szallitolevelek, oek, raktarak, partnerek, arkategoriak]) =>
  //               this.mapSzallitolevelek(
  //                 szallitolevelek,
  //                 oek,
  //                 raktarak,
  //                 partnerek,
  //                 arkategoriak
  //               )
  //             )
  //           )
  //         : JSON.parse(localStorage.getItem('szallitolevel'))?.db > 0 ? this.getDBData() : this.fetchData()
  //     ),
  //     withLatestFrom(this.authService.currentUser$),
  //     map(([data, user]) => data.filter((e: any) => e.oe_id === user.oe))
  //   );
  // }

  get initData$(): Observable<any[]> {
    return this._data.pipe(
      take(1),
      switchMap((state: any[]) =>
        state
          ? forkJoin([
              of(state),
              this.oeService.initData$,
              this.raktarakService.initData$,
              this.partnerekService.initData$,
              this.arkategoriakService.initData$
            ]).pipe(
              map(([szallitolevelek, oek, raktarak, partnerek, arkategoriak]) =>
                this.mapSzallitolevelek(
                  szallitolevelek,
                  oek,
                  raktarak,
                  partnerek,
                  arkategoriak
                )
              )
            )
          : this.fetchData()
      ),
      withLatestFrom(this.authService.currentUser$),
      map(([data, user]) => data.filter((e: any) => e.oe_id === user.oe))
    );
  }

  add(data: any): Observable<any> {
    return this.api.post(this.apiEndpoint, this.toJSON(data)).pipe(
      tap((added: any) => {
        this.stateService.setStateOnAdd(added, this._data);
        //this.dbService.add('szallitolevel', added).subscribe();
      }),
      catchError((err: any) => throwError(err))
    );
  }

  addWithoutMsg(data: any): Observable<any> {
    return this.api.postBizonylat(this.apiEndpoint, this.toJSON(data)).pipe(
      tap((added: any) => {
        this.stateService.setStateOnAdd(added, this._data);
        //this.dbService.add('szallitolevel', added).subscribe();
      }),
      catchError((err: any) => throwError(err))
    );
  }

  delete(id: number): Observable<any> {
    return this.api.delete(this.apiEndpoint, id, 'sztornózás').pipe(
      tap((storno: any) => {
        this.stateService.setStateOnDelete(storno, this._data);
        //this.dbService.deleteByKey('szallitolevel', storno.id).subscribe();
      }),
      catchError((err: any) => throwError(err))
    );
  }

  deleteNoMsg(id: number): Observable<any> {
    return this.api.deleteNoMsg(this.apiEndpoint, id).pipe(
      tap((storno: any) => {
        this.stateService.setStateOnUpdate(storno, this._data);
        //this.dbService.update('szallitolevel', storno).subscribe();
      }),
      catchError((err: any) => throwError(err))
    );
  }

  getBizonylatValtozas(): any {
    this.lastFetch = new Date();
    const oe = JSON.parse(localStorage.getItem('currentUser')).oe;
    const last = JSON.parse(localStorage.getItem('szallitolevel'))?.lastFetch;
    return this.api.postBizonylat(`${this.updateEndpoint}/${oe}/${last}`, "")
           .pipe(
            take(1),
            map(valtozas => {
              if (this._data.value.length == 0) {
                //this.fetchData().subscribe();
              } else {
                if (valtozas.valtozasok.length > 0) {
                  valtozas.valtozasok.forEach((bizonylat) => {
                    const biz = findByProp(this._data.value, "id", bizonylat.id);
                    if (biz) {
                      this.stateService.setStateOnUpdate(bizonylat, this._data);
                    } else {
                      this.stateService.setStateOnAdd(bizonylat, this._data);
                    }
                  });
                }
                if (valtozas.torolt_lista.length > 0) {
                  valtozas.torolt_lista.forEach((bizonylat) => {
                    const biz = findByProp(
                      this._data.value,
                      "id",
                      bizonylat.modul_id
                    );
                    if (biz) {
                      this.stateService.setStateOnDelete(biz, this._data);
                    }
                  });
                }
              }
    
              localStorage.setItem(
                "szallitolevel",
                JSON.stringify({
                  db: this._data.value.length,
                  lastFetch: this.lastFetch,
                })
              );
            }))
  }


  getById(id: string): Observable<any> {
    return this.data$.pipe(
      take(1),
      map((state) => {
        // Find the data
        const data = state.find((item: any) => item.id === +id) || null;

        // Update the data
        this._selectedData.next(data);

        // Return the data
        return data;
      }),
      switchMap((data) => {
        if (id === 'new') {
          return of(null);
        }

        if (!data) {
          return throwError('Could not found data with id of ' + id + '!');
        }

        return of(data);
      })
    );
  }

  fetchAndMapDataById(id: number | string): Observable<any> {
    // Find the data in state
    const data = this._data.value.find((item: any) => item.id === +id) || null;

    // Update the data
    if(data.tetelek) {
      this._selectedData.next(data);
    }

    return data.tetelek ? of(data) : forkJoin([
      this.api.get(`${this.apiEndpoint}/${id}`).pipe(
        catchError((err: any) => {
          console.log(err);
          return of(null);
        })
      ),
      this.oeService.initData$,
      this.raktarakService.initData$,
      this.partnerekService.initData$,
      this.arkategoriakService.initData$,
      this.afakulcsokService.initData$
    ]).pipe(
      map(([szallitolevelek, oek, raktarak, partnerek, arkategoriak, afakulcsok]) => {
        const retVal = this.mapSzallitolevel(szallitolevelek, oek, raktarak, partnerek, arkategoriak, afakulcsok)
        this.stateService.setStateOnUpdate(retVal, this._data);
        this._selectedData.next(retVal);
        return retVal;
      }
      )
    );
  }

  update(data: any): Observable<any> {
    return this.api
      .put(`${this.apiEndpoint}/${data.id}`, this.toJSON(data))
      .pipe(
        tap((updated: any) => {
          this.stateService.setStateOnUpdate(updated, this._data);
          //this.dbService.update('szallitolevel', updated).subscribe();
        }),
        catchError((err: any) => throwError(err))
      );
  }

  sztorno(id: number): Observable<any> {
    return this.api.delete(this.apiEndpoint, id, 'sztornózás').pipe(
      tap((storno: any) => {
        this.stateService.setStateOnUpdate(storno, this._data);
        //this.dbService.update('szallitolevel', storno).subscribe();
      }),
      catchError((err: any) => throwError(err))
    );
  }

  getDBData(): Observable<any> {
    console.log('get szallitolevel from indexedDB')
    // return this.dbService.getAll('szallitolevel').pipe(
    //   tap((data: any[]) => this._data.next(data.sort((a,b) => b.id - a.id))),
    // );
    return forkJoin([
      this.dbService.getAll('szallitolevel').pipe(
        tap((data: any[]) => this._data.next(data.sort((a,b) => b.id - a.id))),
      ),
      this.oeService.initData$,
      this.raktarakService.initData$,
      this.partnerekService.initData$,
      this.arkategoriakService.initData$
    ]).pipe(
      map(([szallitolevelek, oek, raktarak, partnerek, arkategoriak]) => {
        return this.mapSzallitolevelek(szallitolevelek, oek, raktarak, partnerek, arkategoriak)
      })
    );
  }

  fetchData(): Observable<any[]> {
    this.lastFetch = new Date();
    return forkJoin([
      this.api.get(this.apiEndpoint).pipe(
        tap((data: any[]) => this._data.next(data)),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
      ),
      this.oeService.initData$,
      this.raktarakService.initData$,
      this.partnerekService.initData$,
      this.arkategoriakService.initData$
    ]).pipe(
      map(([szallitolevelek, oek, raktarak, partnerek, arkategoriak]) => {
        localStorage.setItem('szallitolevel', JSON.stringify({db: szallitolevelek.length, lastFetch: this.lastFetch}));
        return this.mapSzallitolevelek(szallitolevelek, oek, raktarak, partnerek, arkategoriak)
      })
    );
  }

  // private fetchData(): Observable<any[]> {
  //   this.lastFetch = new Date();
  //   return forkJoin([
  //     this.api.get(this.apiEndpoint).pipe(
  //       tap((data: any[]) => this._data.next(data)),
  //       catchError((err: any) => {
  //         console.log(err);
  //         return of([]);
  //       })
  //     ),
  //     this.oeService.initData$,
  //     this.raktarakService.initData$,
  //     this.partnerekService.initData$,
  //     this.arkategoriakService.initData$
  //   ]).pipe(
  //     map(([szallitolevelek, oek, raktarak, partnerek, arkategoriak]) => {
  //       // this.dbService.count('szallitolevel').subscribe(count => {
  //       //   if(count == 0) {
  //       //     this.dbService
  //       //       .bulkAdd('szallitolevel', this.mapSzallitolevelek(szallitolevelek, oek, raktarak, partnerek, arkategoriak))
  //       //       .subscribe((result) => {
                
  //       //       }, error => console.log(error));
  //       //   }
  //       //   localStorage.setItem('szallitolevel', JSON.stringify({db: szallitolevelek.length, lastFetch: this.lastFetch}));
  //       //   //this.cikkek = count;
  //       // })
  //       localStorage.setItem('szallitolevel', JSON.stringify({db: szallitolevelek.length, lastFetch: this.lastFetch}));

  //       return this.mapSzallitolevelek(szallitolevelek, oek, raktarak, partnerek, arkategoriak)
  //     })
  //   );
  // }

  public fetchNyitottSzallito(partner_id: any): Observable<any[]> {
    return forkJoin([
      this.api.get(`${this.nyitottSzallito}/${partner_id}`).pipe(
        tap((data: any[]) => {}),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
      ),
      this.oeService.initData$,
      this.raktarakService.initData$,
      this.partnerekService.initData$,
      this.arkategoriakService.initData$
    ]).pipe(
      map(([szallitolevelek, oek, raktarak, partnerek, arkategoriak]) =>
        this.mapSzallitolevelek(szallitolevelek, oek, raktarak, partnerek, arkategoriak)
      )
    );
  }

  private getColumns(): Observable<Column[]> {
      return this.api.get(this.columnsEndpoint).pipe(
        map((columns: Column[]) => {
         const mappedColumns =  [
            ...columns,
            { name: 'tetelek', nullable: false, hidden: true },
            { name: 'oe_id', nullable: true },
            { name: 'megjegyzes_nyomtat', nullable: false, hidden: true, default: 1 },
            { name: 'szallitasi_cim_json', nullable: true, hidden: true}
          ]
          localStorage.setItem('szallitolevel_columns', JSON.stringify(mappedColumns));
          return mappedColumns;
        }),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
      );
  }

  private mapSzallitolevelek(
    szallitolevelek: any[],
    oek: any[],
    raktarak: any[],
    partnerek: any[],
    arkategoriak: any[]
  ): any[] {
    return szallitolevelek.map((e: any) => ({
      ...e,
      atado_oe: findByProp(oek, 'id', e.atado_oe_id)?.megnevezes,
      atado_raktar: findByProp(raktarak, 'id', e.atado_raktar_id)?.megnevezes,
      partner: findByProp(partnerek, 'id', e.partner_id)?.nev,
      //szamla_kelte: new Date(e.szamla_kelte),
      teljesites_datum: new Date(e.teljesites_datum),
      bizonylat_datuma: new Date(e.bizonylat_datuma),
      //fizetesi_hatarido: new Date(e.fizetesi_hatarido),
      //tetelek: e.tetelek?.map((t: any) => ({ ...t, arkategoria: findByProp(arkategoriak, 'id', t.arkategoria_id)?.nev, })),
      //sztornozhato: e.tetelek?.filter(item => item.kiszamlazva != 0).length > 0 ? false : true,
      sztornozhato: (e.statusz != 3 && e.statusz != 4)
    }));
  }

  private mapSzallitolevel(
    szallitolevel: any,
    oek: any[],
    raktarak: any[],
    partnerek: any[],
    arkategoriak: any[],
    afakulcsok: any[]
  ): any[] {
    return {
      ...szallitolevel,
      atado_oe: findByProp(oek, 'id', szallitolevel.atado_oe_id)?.megnevezes,
      atado_raktar: findByProp(raktarak, 'id', szallitolevel.atado_raktar_id)?.megnevezes,
      partner: findByProp(partnerek, 'id', szallitolevel.partner_id)?.nev,
      //szamla_kelte: new Date(e.szamla_kelte),
      teljesites_datum: new Date(szallitolevel.teljesites_datum),
      bizonylat_datuma: new Date(szallitolevel.bizonylat_datuma),
      //fizetesi_hatarido: new Date(e.fizetesi_hatarido),
      tetelek: szallitolevel.tetelek?.map((t: any) => ({ ...t, arkategoria: findByProp(arkategoriak, 'id', t.arkategoria_id)?.nev, afakulcs_nev: findByProp(afakulcsok, 'id', t.afakulcs_id)?.nev  })),
      //sztornozhato: e.tetelek?.filter(item => item.kiszamlazva != 0).length > 0 ? false : true,
      sztornozhato: (szallitolevel.statusz != 3 && szallitolevel.statusz != 4),
      szallitasi_cim: szallitolevel.szallitasi_cim ? parseJSON(szallitolevel.szallitasi_cim) : null
    };
  }

  private toJSON(data: any): any {
    return {
      ...data,
      teljesites_datum: new Date(new Date(data.teljesites_datum).setHours(new Date(data.teljesites_datum).getHours() + 3)),
      cim: JSON.stringify(data.cim),
      szallitasi_cim: stringifyIfJSON(data.szallitasi_cim)
    };
  }

  generatePdf(payload: { id: number; sztorno?: boolean }): Observable<any> {
    return this.api
      .postBizonylat(`${environment.pdfGeneralas}/${this.apiEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  generatePdfArnelkul(payload: { id: number; sztorno?: boolean }): Observable<any> {
    return this.api
      .post(`${environment.pdfGeneralas}/${this.arNelkulEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  nyomtatasBeallitas(row: any, megjegyzes_nyomtat: number, hivatkozas_nyomtat: number = 0): Observable<any> {
    return this.api
    .post(`${this.nyomtatasBeallitasEndpoint}/${row.id}/${megjegyzes_nyomtat}/${hivatkozas_nyomtat}`, '')
    .pipe(
      tap((updated: any) => {
        const updateRow = {
          ...row,
          megjegyzes_nyomtat: megjegyzes_nyomtat,
        }
        this.stateService.setStateOnUpdate(updateRow, this._data);
      }),
      catchError(() => of(null))
    );
  }

  generateOsszesitoPdf(payload: { id: number[]}): Observable<any> {
    return this.api
      .post(`${environment.pdfGeneralas}/${this.osszesitoNyomtatas}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }
}
